/**
 * Pass-and-play handoff overlay.
 *
 * Renders a full-screen blocker with "Pass to {name}" text. The device must
 * be physically handed to that player; they tap "I am {name}" to dismiss.
 *
 * In tabletop layout, the prompt is duplicated and rotated 180\u00b0 so it reads
 * correctly regardless of which side of the device the receiving player sits.
 *
 * Hand-cover transition: the overlay also serves as a 300ms blur during the
 * transition between players \u2014 mounted before the new viewer's board renders.
 */
import { useEffect, useState } from "react";
import type { ScreenLayout } from "@/types/game";
import { Button } from "@/components/ui/Button";
import { ArrowRight } from "lucide-react";

interface PassScreenProps {
  toPlayerName: string;
  layout: ScreenLayout;
  onReady: () => void;
}

export function PassScreen({ toPlayerName, layout, onReady }: PassScreenProps) {
  // Keep "Ready" disabled for 300ms after mount so the previous viewer
  // can't accidentally double-tap through and reveal the next hand.
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setArmed(true), 300);
    return () => clearTimeout(t);
  }, []);

  const card = (rotated: boolean) => (
    <div
      className="flex flex-col items-center justify-center gap-4 rounded-xl border border-amber-soft/40 bg-[#13151a] px-10 py-12 text-center shadow-[0_0_60px_rgba(245,158,11,0.15)]"
      style={rotated ? { transform: "rotate(180deg)" } : undefined}
    >
      <p className="text-xs uppercase tracking-[0.2em] text-[#94a3b8]">
        Pass the device
      </p>
      <h2 className="font-display text-3xl font-bold text-amber-soft">
        Pass to {toPlayerName}
      </h2>
      <p className="max-w-xs text-sm text-[#94a3b8]">
        Hand the device over. Once {toPlayerName} is holding it, tap below.
      </p>
      <Button onClick={onReady} disabled={!armed} className="mt-2 gap-2">
        I am {toPlayerName} <ArrowRight size={14} />
      </Button>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-bg/95 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      aria-label={`Pass device to ${toPlayerName}`}
    >
      {layout === "tabletop" ? (
        <div className="flex h-full w-full flex-col">
          {/* Top half (rotated 180\u00b0 so it reads from the far side). */}
          <div className="flex flex-1 items-center justify-center">
            {card(true)}
          </div>
          {/* Bottom half (right-side-up). */}
          <div className="flex flex-1 items-center justify-center">
            {card(false)}
          </div>
        </div>
      ) : (
        card(false)
      )}
    </div>
  );
}
